import _ from 'lodash';
import { BehaviorSubject } from 'rxjs';

const DEFAULT_PLAY_TIME = 3 * 60;
const NUM_OF_IMAGES = 10;

export class PlayerService {
  _frame;
  fakeImg = new Image();
  _maxPlayTime = DEFAULT_PLAY_TIME;
  streaming$ = new BehaviorSubject(false);

  _useV1 = false;
  _v1latestIndexReturned = -1;

  constructor(useStreamV1) {

    // setup
    this.msgId = 0;
    this.useV1 = useStreamV1;
    this.hasWorker = Boolean(window.Worker);

    if (this.hasWorker && !this.useV1) {
      this.worker = new Worker(new URL('playerWorker.js', import.meta.url));
      this.worker.addEventListener('message', (...args) => this.handleResponse(...args), { passive: true });
    } else {
      console.log('PlayerService using V1');
    }

    this.maxPlayTime = DEFAULT_PLAY_TIME;
    this.fakeImg.addEventListener(
      'load',
      evt => evt?.target?.src && this._actuallySetFrame(evt.target.src),
      { passive: true }
    );
    this.fakeImg.addEventListener(
      'error',
      (evt) => {
        evt?.target?.src && this._revoke(evt.target.src);
      },
      { passive: true }
    );
  }

  get frame() {
    return this._frame;
  }

  set frame(elem) {
    this._frame = elem;
    this._frame.addEventListener('load', (evt) => { evt?.target?.src && this._revoke(evt.target.src) });
    this._frame.addEventListener('error', (evt) => { evt?.target?.src && this._revoke(evt.target.src) });
  }

  get maxPlayTime() {
    return this._maxPlayTime;
  }

  set maxPlayTime(seconds = DEFAULT_PLAY_TIME) {
    seconds = _.toNumber(seconds);
    this._maxPlayTime = seconds > 0 ? seconds : Infinity;
    this._doIt('updateMaxPlayTime', this.maxPlayTime);
  }

  handleResponse(event) {
    const [msg, data] = event.data;

    if (msg === 'imageUrl') {
      this.fakeImg.src = data;
    }
    if (msg === 'streaming') {
      this.streaming$.next(data);
    }
  }

  _actuallySetFrame(src) {
    if (this.currentFrame) {
      this._revoke(this.currentFrame);
    }
    this.currentFrame = src;
    window.requestAnimationFrame(() => {
      this.frame.src = this.currentFrame;
    });
  }

  startStream(conf) {
    this._doIt('startStream', conf);
  }

  stopStream() {
    this._doIt('stopStream');
  }

  _revoke(url) {
    this._doIt('revoke', url);
  }

  _doIt(cmd, data) {
    if (this.worker) {
      this.worker.postMessage([++this.msgId, cmd, data]);
    }
    const v1cmd = `_v1${cmd}`;
    if (this.useV1 && _.isFunction(this[v1cmd])) {
      this[v1cmd](data);
    }
  }

  destroy() {
    this.stopStream();
    if (this.worker) {
      this.worker.terminate();
    }
  }

  get useV1() {
    return this._useV1;
  }

  set useV1(val) {
    if (val && this.worker) {
      this.destroy(); // if switching to v1 and worker present, terminate worker
    }
    this._useV1 = val;
  }

  _v1startStream({ source, interval }) {
    this._v1stopStream();
    let frameIndex = 0;

    this._v1latestIndexReturned = -1;

    const virtualImages = new VirtualImagesArray(NUM_OF_IMAGES, this);

    this._v1playerInterval = setInterval(() => {
      const imageFrame = virtualImages[frameIndex % NUM_OF_IMAGES];

      // only reuse images that were requested before the most recently returned one
      // this way we don't continually cancel requests before able to load on slow connections
      if (imageFrame.index <= this._v1latestIndexReturned) {
        imageFrame.index = frameIndex;
        imageFrame.src = `${source}&timestamp=${Date.now()}`;
      }
      frameIndex += 1;

    }, interval);

    this._v1setMaxPlayTimer();
    this._v1setStreamingStatus(true);
  }

  _v1stopStream() {
    if (this._v1playerInterval) {
      clearInterval(this._v1playerInterval);
      this._v1playerInterval = null;
    }
    this._v1clearMaxPlayTimer();
    this._v1setStreamingStatus(false);
  }

  _v1updateMaxPlayTime() {
    this._v1setMaxPlayTimer();
  }

  _v1setMaxPlayTimer() {
    this._v1clearMaxPlayTimer();
    if (this.maxPlayTime < Infinity) {
      this._v1maxPlayTimer = setTimeout(() => this._v1stopStream(), this.maxPlayTime * 1000);
    }
  }

  _v1clearMaxPlayTimer() {
    if (this._v1maxPlayTimer) {
      clearTimeout(this._v1maxPlayTimer);
      this._v1maxPlayTimer = null;
    }
  }

  _v1setStreamingStatus(status) {
    this.handleResponse({ data: ['streaming', status] });
  }

}

function VirtualImagesArray(count = 10, service) {
  return _.map(new Array(count), () => new VirtualImage(service));
}

function VirtualImage(service) {
  const img = new Image();
  img.index = -1;
  img.onload = function () {
    if (this.index > service._v1latestIndexReturned) {
      service._v1latestIndexReturned = this.index; // update latest index
      service.handleResponse({ data: ['imageUrl', this.src] });
    }
  };
  img.onerror = function () {
    this.index = -1; // reset index for reuse
  };
  return img;
}
